// CRM Settings screen. When `embedded` (rendered inside the Shell), it skips
// its own provider tree and back-button — the Shell already owns those. Kept
// standalone-mountable for backwards compatibility with the old Preview view.

import React, { useState, useMemo } from "react";
import { ArrowLeft, Users, Clock, FolderKanban, Mail, DollarSign, Loader2, UserCircle } from "lucide-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { IconButton } from "../atoms";
import PageHeader from "../organisms/PageHeader";
import FollowUpSettingsSection from "../organisms/settings/FollowUpSettingsSection";
import EmailFollowUpSettingsSection from "../organisms/settings/EmailFollowUpSettingsSection";
import MembersSection from "../organisms/settings/MembersSection";
import ProjectsSection from "../organisms/settings/ProjectsSection";
import EmailAccountsSection from "../organisms/settings/EmailAccountsSection";
import CurrencySection from "../organisms/settings/CurrencySection";
import GoogleConnectionsSection from "../organisms/settings/GoogleConnectionsSection";
import { CrmThemeProvider } from "../theme/ThemeProvider";
import { crmQueryClient } from "../api/queryClient";
import { ActiveCallProvider } from "../contexts/ActiveCallProvider";
import { useMyCrmRole } from "../hooks/useTeamMembers";

// Tab visibility is role-gated:
//   admin (owner/manager) → all tabs
//   user  (agent/viewer)  → Projects (scoped to assigned ones) + Email (own mailbox)
// Members/Follow-ups/Currency stay admin-only because they're org-wide config.
// Email is per-user (each user connects their own Gmail), so non-admins need
// access to manage their own mailbox connection.
// Companies and Appearance tabs are hidden per product request.
const ALL_TABS = [
  // Members tab hidden — members are managed from Team Management in the main app.
  // { id: "members",    label: "Members",    icon: Users,        adminOnly: true  },
  { id: "projects",   label: "Projects",   icon: FolderKanban, adminOnly: false },
  { id: "email",      label: "Email",      icon: Mail,         adminOnly: false },
  // One tab for every Google account (company-wide, personal, per project).
  // Replaces the old "My Account" tab and the per-project Connections card —
  // they were the same connection at different scopes.
  { id: "google",     label: "Google",     icon: UserCircle,   adminOnly: false },
  { id: "followups",  label: "Follow-ups", icon: Clock,        adminOnly: true  },
  { id: "currency",   label: "Currency",   icon: DollarSign,   adminOnly: true  },
];

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        height: 34, padding: "0 9px", border: "none", background: "transparent",
        cursor: "pointer", fontSize: 13, fontWeight: active ? 500 : 400,
        color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
        borderBottom: `2px solid ${active ? "var(--color-text-secondary)" : "transparent"}`,
        // marginBottom:-1 removed — it pushed the button 1px past the scrollable tab row.
      }}
    >
      <Icon size={14} />
      {tab.label}
    </button>
  );
}

// Read ?projectId=... from the URL. The Meet-connect OAuth callback redirects
// back with this param so Settings can deep-link to the right project's row
// (otherwise the user lands on the Members tab and has to navigate manually).
function projectIdFromUrl() {
  try { return new URLSearchParams(window.location.search).get("projectId") || null; }
  catch { return null; }
}

function SettingsBody({ onBack, embedded }) {
  const { isAdmin, isLoading: roleLoading } = useMyCrmRole();
  const tabs = useMemo(() => ALL_TABS.filter((t) => !t.adminOnly || isAdmin), [isAdmin]);
  // If the URL carries a projectId (post-OAuth redirect from Meet/email
  // connect), force the Projects sub-tab so the user lands where they expect.
  const deepLinkProjectId = projectIdFromUrl();
  const [tab, setTab] = useState(() => {
    if (deepLinkProjectId) return "projects";
    return "projects";
  });

  // If the previously-selected tab gets hidden after role data resolves, fall
  // back to the first visible tab so we never render an "empty" pane.
  React.useEffect(() => {
    if (!tabs.find((t) => t.id === tab)) setTab(tabs[0]?.id || "projects");
  }, [tabs, tab]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader
        title={isAdmin ? "Org settings" : "My settings"}
        subtitle="Projects, mailboxes, follow-up rules and currency"
        actions={!embedded && onBack ? (
          <IconButton aria-label="Back" onClick={onBack}><ArrowLeft size={16} /></IconButton>
        ) : null}
        tabs={tabs.map((t) => (
          <TabButton key={t.id} tab={t} active={tab === t.id} onClick={() => setTab(t.id)} />
        ))}
      />

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {roleLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-secondary)", fontSize: 13 }}>
            <Loader2 size={14} className="animate-spin" /> Loading settings…
          </div>
        ) : (
          // Projects is a master/detail layout that wants the full width to
          // breathe (project list + tabbed detail pane side by side); every
          // other tab is a single settings form, capped for readability.
          <div style={{ maxWidth: tab === "projects" ? "none" : 720 }}>
            {/* {tab === "members" && isAdmin && <MembersSection />} */}
            {tab === "projects" && <ProjectsSection />}
            {tab === "email" && <EmailAccountsSection isAdmin={isAdmin} />}
            {tab === "google" && <GoogleConnectionsSection isAdmin={isAdmin} />}
            {tab === "followups" && isAdmin && (
              <>
                <FollowUpSettingsSection />
                <EmailFollowUpSettingsSection />
              </>
            )}
            {tab === "currency" && isAdmin && <CurrencySection />}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings({ onBack, embedded = false }) {
  if (embedded) return <SettingsBody embedded onBack={onBack} />;
  return (
    <QueryClientProvider client={crmQueryClient}>
      <CrmThemeProvider>
        <ActiveCallProvider>
          <SettingsBody onBack={onBack} embedded={false} />
          <Toaster position="top-right" />
        </ActiveCallProvider>
      </CrmThemeProvider>
    </QueryClientProvider>
  );
}
